import { saveBlob } from "@dl/web-core-js/ui";
import type { HttpClient, JobDTO } from "@dl/web-core-js/api";

/**
 * A production lot's paper: travellers, production sheet, customer-service
 * labels, transfer manifests (PDFs), and Cut Rite cut lists (CSV). Wire names
 * match the Java records in LotDocumentController and CutlistController.
 */

/** The drawing a component was released with, against the part's current one. */
export interface DrawingStatusDTO {
  production_order_component_id: number;
  /** The component's scan code, e.g. 900123-4. */
  code: string;
  part_number: string;
  released_part_file_id: number | null;
  released_revision: string | null;
  current_part_file_id: number | null;
  current_revision: string | null;
  /** Neither released with a drawing nor has one now: the traveller prints a placeholder. */
  is_missing: boolean;
  /** The design office has issued a newer drawing since the lot was released. */
  has_newer: boolean;
}

/** A truckload of the lot, for its manifest. */
export interface TransferRefDTO {
  id: number;
  number: number;
  status: "SHIPPED" | "RECEIVED" | "CANCELLED";
  shipped_date: string;
  truck_reference: string | null;
}

export interface LotDocumentSummaryDTO {
  production_order_id: number;
  number: number;
  component_count: number;
  /** One per component per pallet. */
  traveller_pages: number;
  /** Lot quantity plus the spares; 0 for Kanban and service lots. */
  label_count: number;
  has_painted: boolean;
  drawings: DrawingStatusDTO[];
  transfers: TransferRefDTO[];
  /** When Cut Rite took the cut list; null while it is in the outbox or before release. */
  cutlist_fetched_at: string | null;
}

/** A lot waiting in Cut Rite's outbox. */
export interface CutlistOutboxLotDTO {
  id: number;
  number: number;
  sku: string | null;
  name: string | null;
  released_at: string;
  component_count: number;
}

export type LotDocumentKind = "travellers" | "sheet" | "labels" | "package";

export class LotDocumentClient {
  constructor(private readonly http: HttpClient) {}

  summary(orderId: number): Promise<LotDocumentSummaryDTO> {
    return this.http.get<LotDocumentSummaryDTO>(`/production/document/lot/${String(orderId)}/summary`);
  }

  /** One of a lot's PDFs, with the name the server gives it. */
  lotDocument(orderId: number, kind: LotDocumentKind): Promise<{ blob: Blob; filename: string }> {
    return this.http.download(`/production/document/lot/${String(orderId)}/${kind}.pdf`, { method: "GET" });
  }

  transferManifest(transferId: number): Promise<{ blob: Blob; filename: string }> {
    return this.http.download(`/production/document/transfer/${String(transferId)}/manifest.pdf`, { method: "GET" });
  }

  /** Several lots' travellers and sheets in one PDF, through the job center. */
  lotsPackage(orderIds: number[]): Promise<JobDTO> {
    return this.http.job("/production/document/lots/package", { body: { production_order_ids: orderIds } });
  }

  lotCutlist(orderId: number): Promise<{ blob: Blob; filename: string }> {
    return this.http.download(`/production/cutlist/lot/${String(orderId)}.csv`, { method: "GET" });
  }

  /** A part's cut list for a quantity, without a lot; the server's default quantity when left out. */
  partCutlist(partId: number, quantity?: number): Promise<{ blob: Blob; filename: string }> {
    return this.http.download(`/production/cutlist/part/${String(partId)}.csv`, {
      method: "GET",
      query: { quantity: quantity },
    });
  }

  outbox(): Promise<CutlistOutboxLotDTO[]> {
    return this.http.get<CutlistOutboxLotDTO[]>("/production/cutlist/outbox");
  }

  /** Back into Cut Rite's outbox, to be taken again. */
  putBackInOutbox(orderId: number): Promise<void> {
    return this.http.post<void>(`/production/cutlist/outbox/${String(orderId)}/reset`, {}, { responseType: "void" });
  }
}

/**
 * Opens a PDF in a new tab to print, or saves it when the browser refuses the
 * tab. The tab is opened before the request, while the click still counts as
 * the user's: a tab opened after the download comes back is a popup, and
 * blocked.
 */
export async function openPdf(fetchPdf: () => Promise<{ blob: Blob; filename: string }>): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    const { blob, filename } = await fetchPdf();
    if (tab) {
      tab.location.href = URL.createObjectURL(blob);
    } else {
      saveBlob(blob, filename);
    }
  } catch (failure) {
    tab?.close();
    throw failure;
  }
}

/** Saves a downloaded file under the server's name. */
export async function saveDownload(fetchFile: () => Promise<{ blob: Blob; filename: string }>): Promise<void> {
  const { blob, filename } = await fetchFile();
  saveBlob(blob, filename);
}
